import { cloneElement, useId, useState, type ReactElement } from 'react';
import { cn } from '../utils/cn';

export interface TooltipProps {
  content: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** The trigger. Must accept a ref-free set of DOM props and render an element. */
  children: ReactElement;
  className?: string;
}

const placementClasses: Record<NonNullable<TooltipProps['placement']>, string> = {
  top: 'bottom-full left-1/2 mb-2 -translate-x-1/2',
  bottom: 'top-full left-1/2 mt-2 -translate-x-1/2',
  left: 'right-full top-1/2 mr-2 -translate-y-1/2',
  right: 'left-full top-1/2 ml-2 -translate-y-1/2',
};

/**
 * Opens on hover and on focus, and closes on Escape, so a keyboard user gets
 * the same information a mouse user does. Tooltips never carry the only copy of
 * something important — they supplement a visible label.
 */
export function Tooltip({ content, placement = 'top', children, className }: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);

  const trigger = cloneElement(children, {
    'aria-describedby': open ? id : undefined,
    onMouseEnter: () => setOpen(true),
    onMouseLeave: () => setOpen(false),
    onFocus: () => setOpen(true),
    onBlur: () => setOpen(false),
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
      (children.props as { onKeyDown?: (e: React.KeyboardEvent) => void }).onKeyDown?.(event);
    },
  } as Partial<React.HTMLAttributes<HTMLElement>>);

  return (
    <span className={cn('relative inline-flex', className)}>
      {trigger}
      {open ? (
        <span
          id={id}
          role="tooltip"
          className={cn(
            'absolute z-40 max-w-xs animate-pvi-fade-in rounded-md bg-navy px-2.5 py-1.5',
            'leading-dense text-dense font-normal text-navy-contrast shadow-overlay',
            placementClasses[placement],
          )}
        >
          {content}
        </span>
      ) : null}
    </span>
  );
}
